import productStore from '../../stores/product/ProductStore';
import httpRequest from '../../utilities/js/httpRequest/httpRequest';
import productService from './ProductService';
import pigeon from '../../utilities/js/pigeon/pigeon';
import {TrackingService} from '../tracking/TrackingService';
import {documentHelper} from '../../utilities/js/documentHelper/documentHelper';
import type RecurringOrderFrequency from '../../types/RecurringOrderFrequency';
import type AdsSeoInfo from '../../types/AdsSeoInfo';

class ProductPageService {
  protected urls: {
    addToWishlist: string,
    initialChangeVariation: string,
    changeVariation: string,
    changeSaleboxQuantity: string
  };

  constructor() {
    this.urls = window.__globalConfig.apiUrls.product;

    if (window.__productConfig) {
      const prefix = window.__productConfig.apiUrls.product.prefix;
      const productId = window.__productConfig.apiUrls.product.productId;
      this.urls.initialChangeVariation = `/${prefix}/${window.__productConfig.apiUrls.product.initialChangeVariation}/?productId=${productId}`;
      this.urls.changeVariation = `/${prefix}/${window.__productConfig.apiUrls.product.changeVariation}/?productId=${productId}`;
      this.urls.changeSaleboxQuantity = `/${prefix}/${window.__productConfig.apiUrls.product.changeQuantity}/?productId=${productId}`;
    }
  }

  addToBasketFromStore(quantity: number, recurringOrderFrequency?: RecurringOrderFrequency) {
    const product = productStore.productDetails.product;
    return productService.addToBasket(product, quantity, 'productPage', recurringOrderFrequency)
      .then(response => {
        if (product.soldoutRelation && product.soldoutRelation.productId) {
          productStore.productDetails.product.successorInBasket = true;
          return;
        }
        productStore.productDetails = response.data.productDetails;
        if (response.data.upsellQuantity) {
          productStore.upsellQuantity = response.data.upsellQuantity;
        }
      });
  }

  addToWishlistFromStore(productId: string, itemId: string) {
    return productService.addToWishlist(productId, itemId, 'productPage')
      .then((response) => {
        productStore.addToWishlist();
        return response;
      });
  }

  removeFromWishlist(productId: string, itemId: string) {
    return productService.removeFromWishlist(productId, itemId, 'productPage')
      .then((response) => {
        if (!response.data.isInWishlist) {
          productStore.removeFromWishlist();
        }
        return response;
      });
  }

  getProductAlternatives(articleNo: string) {
    return productService.getProductAlternatives(articleNo)
      .then((response) => {
        productStore.productAlternativesLength = (response.data && response.data.productBoxData) ? response.data.productBoxData.length : 0;
        return response;
      });
  }

  getThirdPartyProductAlternatives(articleNo: string) {
    return productService.getThirdPartyProductAlternatives(articleNo);
  }

  initialChangeVariation(itemIdFromUrl: string = null, appendIdToUrl = true) {
    if (itemIdFromUrl) {
      this.internalChangeVariation(this.urls.initialChangeVariation, itemIdFromUrl, productStore.selectedQty, false)
        .then(() => pigeon.publish('productPage::variationChange', {itemIdFromUrl}));
    } else {
      const {artNr, itemId} = productStore.productDetails.product;
      if (appendIdToUrl) {
        this.updateURL(artNr);
      }
      documentHelper.ready(() => {
        pigeon.publish('productPage::variationChange', {itemId});
      });
      TrackingService.trigger(TrackingService.EVENT_PRODUCT_VIEW, productStore.trackingData);
    }
  }

  changeVariation(itemId: string, quantity: number, skipTracking: boolean = false): Promise<any> {
    return this.internalChangeVariation(this.urls.changeVariation, itemId, quantity, skipTracking)
      .then(() => pigeon.publish('productPage::variationChange', {itemId}));
  }

  changeQuantity(itemId: string, quantity: number, skipTracking: boolean = false): Promise<any> {
    if (typeof quantity === 'undefined') {
      const err = {
        name: '',
        message: `Quantity undefined (itemId=${itemId})`,
        stack: '',
      };
      window.onerror(err.message, 'ProductPageService::changeQuantity', null, null, err);
      quantity = 1;
    }

    return this.internalChangeVariation(this.urls.changeSaleboxQuantity, itemId, quantity, skipTracking);
  }

  private internalChangeVariation(url: string, itemId: string, quantity: number, skipTracking: boolean = false) {
    let changeUrl = url.concat(`&itemId=${itemId}&quantity=${quantity}`);
    return httpRequest(changeUrl)
      .then((response) => {
        const responseProductStoreData = response.data;
        productStore.setData(responseProductStoreData);
        this.updateURL(productStore.productDetails.product.artNr);
        this.updateMeta(productStore.adsSeoInfo);

        if (!skipTracking) {
          TrackingService.trigger(TrackingService.EVENT_PRODUCT_VIEW, productStore.trackingData);
        }
      });
  }

  private updateMeta(adsSeoInfo: AdsSeoInfo) {
    if (adsSeoInfo) {
      document.querySelector('title').innerText = adsSeoInfo.title;
      document.getElementsByTagName('meta')['description'].content = adsSeoInfo.metaDescription;
      document.getElementsByTagName('meta')['keywords'].content = adsSeoInfo.metaKeywords;
    }
  }

  private updateURL(artNr: string) {
    let newUrl;
    if (window.location.href.indexOf('articleNo=') > 0) {
      const idToReplace = window.location.href.match(/articleNo=(\d+)/)[1];
      newUrl = window.location.href.replace(idToReplace, `${artNr}`);
    } else {
      const separator = window.location.href.indexOf('?') === -1 ? '?' : '&';
      newUrl = window.location.href.concat(`${separator}articleNo=${artNr}`);
    }
    // remove parameter of old url pattern
    newUrl = newUrl.replace(/#itemId=\d+/, '');

    window.history.replaceState(null, '', newUrl);
  }
}


const productPageService = new ProductPageService();
export default productPageService;
